/**
 * Form Daftar Hadir Event Publik
 * Halaman publik untuk mengisi daftar hadir event
 * Dapat diakses tanpa login dengan link: DOMAIN_ESA_OFFICE/#/publik/events/daftar-hadir/{eventId}
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

// @mui material components
import CircularProgress from "@mui/material/CircularProgress";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Icons
import CloseIcon from "@mui/icons-material/Close";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React themes
import theme from "assets/theme";

// Images
import esaOfficeBanner from "assets/images/eo_cover.png";
import eo_mini1 from "assets/images/eo_mini1.png";

// Utils
import { API_ESA_OFFICE } from "utils";
import Icon from "@mui/material/Icon";

function DaftarHadirEventPublik() {
  // Event ID dari URL parameter
  const { eventId } = useParams();
  const navigate = useNavigate();

  // State untuk deteksi mobile
  const [isMobile, setIsMobile] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [isDateValid, setIsDateValid] = useState(false);
  const [ipAddress, setIpAddress] = useState(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [formData, setFormData] = useState({
    nama: "",
    instansi: "",
  });

  // State untuk list dinas
  const [dinasList, setDinasList] = useState([]);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

  // State untuk dialog rating
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0); // 0 = belum dipilih, 1-5 = rating

  // State untuk Autocomplete detail event
  const [availableDetailEvents, setAvailableDetailEvents] = useState([]);

  // State untuk menyimpan mapping dinas_id ke nama_dinas
  const [dinasMap, setDinasMap] = useState({});

  // State untuk menandai apakah nama dan instansi harus disabled
  const [isFormDisabled, setIsFormDisabled] = useState(false);

  // Fungsi untuk mendapatkan IP address
  const fetchIPAddress = async () => {
    try {
      // Prioritas 1: Coba backend endpoint sendiri terlebih dahulu
      try {
        const backendResponse = await axios.get(
          `${API_ESA_OFFICE}events/client-ip`,
          {
            timeout: 5000,
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (backendResponse.data?.ip_address) {
          const ip = backendResponse.data.ip_address?.trim();
          // Accept all IP addresses including localhost (::1, 127.0.0.1)
          if (
            ip &&
            typeof ip === "string" &&
            (ip === "::1" ||
              ip === "127.0.0.1" ||
              ip === "localhost" ||
              /^(\d{1,3}\.){3}\d{1,3}$/.test(ip) ||
              /^([0-9a-fA-F]{0,4}:){1,7}[0-9a-fA-F]{0,4}$/.test(ip))
          ) {
            setIpAddress(ip);
            console.log("✅ IP address berhasil diambil dari backend:", ip);
            return ip;
          }
        }
      } catch (backendError) {
        console.warn(
          "⚠️ Backend IP endpoint gagal, mencoba API eksternal...",
          backendError.message
        );
      }

      // Prioritas 2: API eksternal sebagai fallback
      const externalApis = [
        {
          url: "https://api.ipify.org?format=json",
          extract: (data) => data?.ip,
        },
        {
          url: "https://api64.ipify.org?format=json",
          extract: (data) => data?.ip,
        },
        {
          url: "https://api.ipify.org",
          extract: (data) => {
            if (typeof data === "string") {
              return data.trim();
            }
            return data?.ip;
          },
        },
        {
          url: "https://api.ip.sb/ip",
          extract: (data) => {
            if (typeof data === "string") {
              return data.trim();
            }
            return data?.ip;
          },
        },
      ];

      for (const api of externalApis) {
        try {
          const response = await axios.get(api.url, {
            timeout: 5000,
            headers: {
              Accept: api.url.includes("ipify")
                ? "application/json"
                : "text/plain",
            },
            transformResponse: [
              (data) => {
                try {
                  return JSON.parse(data);
                } catch {
                  return data;
                }
              },
            ],
          });

          let ip = null;

          // Extract IP using the provided function
          if (typeof api.extract === "function") {
            ip = api.extract(response.data);
          } else if (response.data?.ip) {
            ip = response.data.ip;
          } else if (typeof response.data === "string") {
            ip = response.data.trim();
          }

          // Validate IP address format (accept all including localhost)
          if (
            ip &&
            typeof ip === "string" &&
            (/^(\d{1,3}\.){3}\d{1,3}$/.test(ip) ||
              /^([0-9a-fA-F]{0,4}:){1,7}[0-9a-fA-F]{0,4}$/.test(ip) ||
              ip === "::1" ||
              ip === "127.0.0.1" ||
              ip === "localhost")
          ) {
            setIpAddress(ip);
            console.log(
              "✅ IP address berhasil diambil dari API eksternal:",
              ip
            );
            return ip;
          } else {
            console.warn(`⚠️ Invalid IP format from ${api.url}:`, ip);
          }
        } catch (err) {
          // Log error but continue to next API
          if (err.code === "ECONNABORTED") {
            console.warn(`⏱️ Timeout fetching IP from ${api.url}`);
          } else if (err.response) {
            console.warn(
              `❌ Error ${err.response.status} from ${api.url}:`,
              err.response.statusText
            );
          } else {
            console.warn(`❌ Failed to fetch IP from ${api.url}:`, err.message);
          }
          continue;
        }
      }

      console.warn("⚠️ Could not fetch IP address from any source");
      // Return null jika semua gagal, backend akan handle IP saat submit
      return null;
    } catch (error) {
      console.error("❌ Error fetching IP address:", error);
      // Return null jika semua gagal, backend akan handle IP saat submit
      return null;
    }
  };

  // Fungsi untuk mendapatkan device fingerprint
  const fetchDeviceFingerprint = async () => {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const visitorId = result.visitorId;
      setDeviceFingerprint(visitorId);
      console.log("✅ Device fingerprint berhasil diambil:", visitorId);
      return visitorId;
    } catch (error) {
      console.error("❌ Error fetching device fingerprint:", error);
      return null;
    }
  };

  // Deteksi ukuran layar untuk mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960); // md breakpoint biasanya 960px
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Pilih banner berdasarkan ukuran layar
  const bannerImage = isMobile ? eo_mini1 : esaOfficeBanner;

  // Fetch list dinas
  useEffect(() => {
    const fetchDinasList = async () => {
      try {
        const response = await axios.get(`${API_ESA_OFFICE}dinas`);
        if (response.data && Array.isArray(response.data)) {
          setDinasList(response.data.map((d) => d.nama_dinas || d.nama));
          // Buat mapping dinas_id ke nama_dinas untuk akses cepat
          const mapping = {};
          response.data.forEach((d) => {
            if (d.dinas_id) {
              mapping[d.dinas_id] = d.nama_dinas || d.nama || "";
            }
          });
          setDinasMap(mapping);
        }
      } catch (error) {
        console.error("Error fetching dinas list:", error);
      }
    };
    fetchDinasList();
  }, []);

  useEffect(() => {
    if (eventId) {
      fetchEventData();
      // Ambil IP address saat component mount
      fetchIPAddress();
      // Ambil device fingerprint saat component mount
      fetchDeviceFingerprint();
    }
  }, [eventId]);

  // Fungsi untuk memvalidasi waktu event
  const validateEventTime = (waktu_mulai, waktu_finish) => {
    const now = new Date();
    const startTime = new Date(waktu_mulai);
    const finishTime = waktu_finish ? new Date(waktu_finish) : null;

    // Jika waktu saat ini sebelum waktu mulai
    if (now.getTime() < startTime.getTime()) {
      return {
        isValid: false,
        message: "Event belum mulai",
      };
    }

    // Jika ada waktu finish dan waktu saat ini setelah waktu finish
    if (finishTime && now.getTime() > finishTime.getTime()) {
      return {
        isValid: false,
        message: "Event sudah selesai",
      };
    }

    // Waktu dalam range
    return {
      isValid: true,
      message: null,
    };
  };

  const fetchEventData = async () => {
    setLoading(true);
    try {
      // 1. Fetch event berdasarkan eventId
      const eventResponse = await axios.get(
        `${API_ESA_OFFICE}events/${eventId}`
      );
      const eventData = eventResponse.data;

      if (!eventData) {
        swal({
          title: "Error",
          text: "Event tidak ditemukan",
          icon: "error",
          button: "OK",
        });
        setLoading(false);
        return;
      }

      setEvent(eventData);

      // Cek scope event - jika internal atau pemda, harus login
      if (eventData.scope === "internal" || eventData.scope === "pemda") {
        try {
          const user = JSON.parse(localStorage.getItem("user"));
          if (!user) {
            swal({
              title: "Akses Dibatasi",
              text: "Event ini hanya dapat diakses oleh pengguna yang sudah login. Silakan login terlebih dahulu.",
              icon: "warning",
              button: "Login",
            }).then(() => {
              navigate("/authentication/sign-in");
            });
            setLoading(false);
            return;
          }
        } catch (e) {
          // Jika error parsing localStorage, berarti belum login
          swal({
            title: "Akses Dibatasi",
            text: "Event ini hanya dapat diakses oleh pengguna yang sudah login. Silakan login terlebih dahulu.",
            icon: "warning",
            button: "Login",
          }).then(() => {
            navigate("/authentication/sign-in");
          });
          setLoading(false);
          return;
        }
      }

      // 2. Fetch detail events untuk event ini
      const detailResponse = await axios.get(
        `${API_ESA_OFFICE}events/${eventId}/detail`
      );
      const detailEvents = detailResponse.data || [];

      if (detailEvents.length === 0) {
        swal({
          title: "Error",
          text: "Detail event tidak ditemukan",
          icon: "error",
          button: "OK",
        });
        setLoading(false);
        return;
      }

      // 3. Logika khusus untuk event asistensi atau evaluasi
      const isAsistensiOrEvaluasi =
        eventData.status_event === "asistensi" ||
        eventData.status_event === "evaluasi";

      // Ambil dinas_id dari event (bukan dari user)
      const eventDinasId = eventData.dinas_id;

      // Cek apakah user adalah admin event (dinas_id sama dengan event.dinas_id)
      let isUserAdmin = false;
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user && user.dinas_id && eventDinasId) {
          isUserAdmin = user.dinas_id === eventDinasId;
        }
      } catch (e) {
        // User tidak login, bukan admin
        isUserAdmin = false;
      }

      // Jika status asistensi/evaluasi DAN user BUKAN admin DAN scope BUKAN publik, validasi dinas_id
      // Jika scope adalah "publik", tidak perlu login meskipun status evaluasi/asistensi
      if (
        isAsistensiOrEvaluasi &&
        !isUserAdmin &&
        eventData.scope !== "publik"
      ) {
        try {
          const user = JSON.parse(localStorage.getItem("user"));
          if (user && user.dinas_id) {
            // User bukan admin, cek apakah dinas_id berbeda
            if (user.dinas_id !== eventDinasId) {
              swal({
                title: "Akses Dibatasi",
                text: "Anda tidak memiliki akses untuk event ini. Event ini hanya untuk instansi tertentu.",
                icon: "warning",
                button: "OK",
              });
              setLoading(false);
              return;
            }
          } else {
            // User tidak login, tidak bisa akses event asistensi/evaluasi (kecuali admin)
            swal({
              title: "Akses Dibatasi",
              text: "Event ini hanya dapat diakses oleh pengguna yang sudah login dari instansi terkait. Silakan login terlebih dahulu.",
              icon: "warning",
              button: "Login",
            }).then(() => {
              navigate("/authentication/sign-in");
            });
            setLoading(false);
            return;
          }
        } catch (e) {
          // User tidak login, tidak bisa akses event asistensi/evaluasi (kecuali admin)
          swal({
            title: "Akses Dibatasi",
            text: "Event ini hanya dapat diakses oleh pengguna yang sudah login dari instansi terkait. Silakan login terlebih dahulu.",
            icon: "warning",
            button: "Login",
          }).then(() => {
            navigate("/authentication/sign-in");
          });
          setLoading(false);
          return;
        }
      }

      // Set data dari user yang login jika ada (SEBELUM early return)
      // Ini harus dieksekusi terlepas dari jumlah detail events
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user) {
          setIsUserLoggedIn(true);

          // SELALU set nama dari user jika user sudah login (langsung set)
          setFormData((prev) => ({
            ...prev,
            nama: user.nama || "",
          }));

          // Set instansi dari nama_dinas user jika ada
          if (user.nama_dinas) {
            setFormData((prev) => ({
              ...prev,
              instansi: user.nama_dinas,
            }));
          } else if (user.dinas_id) {
            // Jika tidak ada nama_dinas, fetch dari API (async)
            axios
              .get(`${API_ESA_OFFICE}dinas/${user.dinas_id}`)
              .then((dinasResponse) => {
                if (dinasResponse.data && dinasResponse.data.nama_dinas) {
                  setFormData((prev) => ({
                    ...prev,
                    instansi: dinasResponse.data.nama_dinas,
                  }));
                }
              })
              .catch((dinasError) => {
                console.error("Error fetching dinas:", dinasError);
                // Nama sudah di-set, instansi akan tetap kosong jika gagal
              });
          }

          // Cek kondisi khusus: jika status_event evaluasi/asistensi DAN event.dinas_id == user.dinas_id
          // Maka disable form (nama dan instansi tidak bisa dirubah)
          const shouldDisableForm =
            isAsistensiOrEvaluasi &&
            eventDinasId &&
            user.dinas_id &&
            eventDinasId === user.dinas_id;

          if (shouldDisableForm) {
            // Kondisi khusus: evaluasi/asistensi + dinas_id match
            // Disable form (nama dan instansi tidak bisa dirubah)
            setIsFormDisabled(true);
          } else {
            // Kondisi normal: user login tapi bukan kondisi khusus
            // Form bisa diubah (tidak disabled)
            setIsFormDisabled(false);
          }
        } else {
          setIsUserLoggedIn(false);
          setIsFormDisabled(false);
        }
      } catch (e) {
        // Ignore jika tidak ada user di localStorage
        setIsUserLoggedIn(false);
        setIsFormDisabled(false);
      }

      let closestDetailEvent;

      // Jika detail events lebih dari satu, simpan untuk Autocomplete (tidak set detailEvent langsung)
      if (detailEvents.length > 1) {
        // Simpan detail events untuk Autocomplete
        setAvailableDetailEvents(detailEvents);
        // Jangan set detailEvent, biarkan user memilih dari Autocomplete
        setLoading(false);
        return;
      }

      // Jika hanya 1 detail event, langsung set dan validasi
      if (detailEvents.length === 1) {
        closestDetailEvent = detailEvents[0];
        setDetailEvent(closestDetailEvent);

        // Validasi waktu event (termasuk jam dan menit)
        const timeValidation = validateEventTime(
          closestDetailEvent.waktu_mulai,
          closestDetailEvent.waktu_finish
        );
        setIsDateValid(timeValidation.isValid);

        if (!timeValidation.isValid) {
          const startDate = new Date(closestDetailEvent.waktu_mulai);
          const finishDate = closestDetailEvent.waktu_finish
            ? new Date(closestDetailEvent.waktu_finish)
            : null;

          let messageText = timeValidation.message;
          if (finishDate) {
            messageText += `. Event dijadwalkan pada ${startDate.toLocaleDateString(
              "id-ID",
              {
                day: "numeric",
                month: "long",
                year: "numeric",
              }
            )} pukul ${startDate.toLocaleTimeString("id-ID", {
              hour: "2-digit",
              minute: "2-digit",
            })} - ${finishDate.toLocaleTimeString("id-ID", {
              hour: "2-digit",
              minute: "2-digit",
            })} WIB.`;
          } else {
            messageText += `. Event dijadwalkan pada ${startDate.toLocaleDateString(
              "id-ID",
              {
                day: "numeric",
                month: "long",
                year: "numeric",
              }
            )} pukul ${startDate.toLocaleTimeString("id-ID", {
              hour: "2-digit",
              minute: "2-digit",
            })} WIB.`;
          }

          swal({
            title: "Informasi",
            text: messageText,
            icon: "info",
            button: "OK",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching event data:", error);
      swal({
        title: "Error",
        text: "Gagal memuat data event",
        icon: "error",
        button: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInstansiChange = (event, newValue) => {
    // newValue bisa berupa string (jika freeSolo) atau null
    setFormData((prev) => ({
      ...prev,
      instansi: newValue || "",
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nama || !formData.nama.trim()) {
      swal({
        title: "Error",
        text: "Nama harus diisi",
        icon: "error",
        button: "OK",
      });
      return;
    }

    if (!detailEvent || !detailEvent.id) {
      swal({
        title: "Error",
        text: "Data detail event tidak valid",
        icon: "error",
        button: "OK",
      });
      return;
    }

    if (!event || !event.id) {
      swal({
        title: "Error",
        text: "Data event tidak valid",
        icon: "error",
        button: "OK",
      });
      return;
    }

    // Validasi waktu event sebelum submit
    const timeValidation = validateEventTime(
      detailEvent.waktu_mulai,
      detailEvent.waktu_finish
    );

    if (!timeValidation.isValid) {
      const startDate = new Date(detailEvent.waktu_mulai);
      const finishDate = detailEvent.waktu_finish
        ? new Date(detailEvent.waktu_finish)
        : null;

      let messageText = timeValidation.message;
      if (finishDate) {
        messageText += `. Event dijadwalkan pada ${startDate.toLocaleDateString(
          "id-ID",
          {
            day: "numeric",
            month: "long",
            year: "numeric",
          }
        )} pukul ${startDate.toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${finishDate.toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        })} WIB.`;
      } else {
        messageText += `. Event dijadwalkan pada ${startDate.toLocaleDateString(
          "id-ID",
          {
            day: "numeric",
            month: "long",
            year: "numeric",
          }
        )} pukul ${startDate.toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        })} WIB.`;
      }

      swal({
        title: "Informasi",
        text: messageText,
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Langsung buka dialog rating tanpa konfirmasi
    setSelectedRating(0);
    setRatingDialogOpen(true);
  };

  const handleRatingSubmit = async () => {
    if (selectedRating === 0) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih penilaian terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    setRatingDialogOpen(false);
    setSubmitting(true);
    try {
      // Cek apakah user sudah login
      let uid_RSS = null;
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user && user.uid_RSS) {
          uid_RSS = user.uid_RSS;
        }
      } catch (e) {
        // User tidak login, lanjutkan sebagai publik
      }

      // Pastikan IP address sudah diambil, jika belum coba ambil lagi
      let finalIPAddress = ipAddress;
      if (!finalIPAddress) {
        finalIPAddress = await fetchIPAddress();
      }

      // Pastikan device fingerprint sudah diambil, jika belum coba ambil lagi
      let finalDeviceFingerprint = deviceFingerprint;
      if (!finalDeviceFingerprint) {
        finalDeviceFingerprint = await fetchDeviceFingerprint();
      }

      if (!finalDeviceFingerprint) {
        swal({
          title: "Error",
          text: "Gagal mendapatkan device fingerprint. Silakan refresh halaman dan coba lagi.",
          icon: "error",
          button: "OK",
        });
        setSubmitting(false);
        return;
      }

      const payload = {
        id_events: event.id,
        uid_RSS: uid_RSS,
        nama: formData.nama.trim(),
        instansi: formData.instansi ? formData.instansi.trim() : null,
        ip_address: finalIPAddress || null,
        device_fingerprint: finalDeviceFingerprint,
        nilai: selectedRating,
        status_event: event.status_event || null,
      };

      await axios.post(
        `${API_ESA_OFFICE}attendance/${detailEvent.id}`,
        payload
      );

      swal({
        title: "Terima Kasih!",
        html: `
          <div style="text-align: center; padding: 20px;">
            <div style="font-size: 64px; margin-bottom: 20px;">🙏</div>
            <p style="font-size: 16px; line-height: 1.6; margin: 10px 0;">
              Terima kasih sudah mengisi daftar hadir.
            </p>
            <p style="font-size: 16px; line-height: 1.6; margin: 10px 0;">
              Kehadiran Anda telah tercatat dengan baik.
            </p>
            <p style="font-size: 14px; line-height: 1.6; margin: 10px 0; color: #666;">
              Terima kasih atas penilaian Anda.
            </p>
          </div>
        `,
        icon: "success",
        button: "OK",
      }).then(() => {
        // Reset form
        setFormData({
          nama: formData.nama || "", // Keep nama if exists
          instansi: formData.instansi || "", // Keep instansi if exists
        });
        setSelectedRating(0);
      });
    } catch (error) {
      console.error("Error submitting attendance:", error);
      swal({
        title: "Error",
        text: error.response?.data?.message || "Gagal mengirimkan daftar hadir",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
  };

  const handleCancelRating = async () => {
    const cancelResult = await swal({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin membatalkan? Penilaian wajib diisi untuk melanjutkan.",
      icon: "warning",
      buttons: ["Tidak", "Ya, Batalkan"],
      dangerMode: true,
    });

    if (cancelResult) {
      setRatingDialogOpen(false);
      setSelectedRating(0);
    }
  };

  // Handler untuk memilih detail event dari Autocomplete
  const handleSelectDetailEvent = (event, newValue) => {
    if (newValue) {
      setDetailEvent(newValue);

      // Validasi waktu event setelah dipilih
      const timeValidation = validateEventTime(
        newValue.waktu_mulai,
        newValue.waktu_finish
      );
      setIsDateValid(timeValidation.isValid);

      if (!timeValidation.isValid) {
        const startDate = new Date(newValue.waktu_mulai);
        const finishDate = newValue.waktu_finish
          ? new Date(newValue.waktu_finish)
          : null;

        let messageText = timeValidation.message;
        if (finishDate) {
          messageText += `. Event dijadwalkan pada ${startDate.toLocaleDateString(
            "id-ID",
            {
              day: "numeric",
              month: "long",
              year: "numeric",
            }
          )} pukul ${startDate.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          })} - ${finishDate.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          })} WIB.`;
        } else {
          messageText += `. Event dijadwalkan pada ${startDate.toLocaleDateString(
            "id-ID",
            {
              day: "numeric",
              month: "long",
              year: "numeric",
            }
          )} pukul ${startDate.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          })} WIB.`;
        }

        swal({
          title: "Informasi",
          text: messageText,
          icon: "info",
          button: "OK",
        });
      }
    }
  };

  // Fungsi untuk mendapatkan label detail event untuk Autocomplete
  const getDetailEventLabel = (detailEvent) => {
    if (!detailEvent) return "";
    const startDate = new Date(detailEvent.waktu_mulai);
    const detailKegiatan = detailEvent.detail_kegiatan || "Detail Kegiatan";
    const waktu = startDate.toLocaleString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    // Tampilkan nama dinas dari dinas_id, bukan tempat
    const namaDinas = detailEvent.dinas_id
      ? dinasMap[detailEvent.dinas_id] || `Dinas ID: ${detailEvent.dinas_id}`
      : "-";
    return `${detailKegiatan} - ${namaDinas} - ${waktu}`;
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
            <MDTypography variant="body2" color="text" ml={2}>
              Memuat data...
            </MDTypography>
          </MDBox>
        </Container>
      </ThemeProvider>
    );
  }

  if (!event) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
          <Card>
            <CardContent>
              <MDTypography variant="h6" color="error" textAlign="center">
                Event tidak ditemukan atau tidak valid
              </MDTypography>
            </CardContent>
          </Card>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          {/* Banner Header */}
          <MDBox
            sx={{
              position: "relative",
              width: "100%",
              height: "200px",
              overflow: "hidden",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <img
              src={bannerImage}
              alt="ESA Office Banner"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </MDBox>

          <MDBox p={4}>
            {/* Judul */}
            <MDTypography
              variant="h4"
              fontWeight="bold"
              mb={3}
              textAlign="center"
            >
              Daftar Hadir Event
            </MDTypography>

            {/* Informasi Event */}
            <MDBox mb={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Informasi Event
              </MDTypography>
              <MDBox mb={1}>
                <MDTypography variant="body2" color="text.secondary">
                  <strong>Kegiatan:</strong> {event.kegiatan || "-"}
                </MDTypography>
              </MDBox>
              {detailEvent && (
                <>
                  <MDBox mb={1}>
                    <MDTypography variant="body2" color="text.secondary">
                      <strong>Detail Kegiatan:</strong>{" "}
                      {detailEvent.detail_kegiatan || "-"}
                    </MDTypography>
                  </MDBox>
                  <MDBox mb={1} display="flex" gap={2} flexWrap="wrap">
                    <MDTypography variant="body2" color="text.secondary">
                      <strong>Waktu Mulai:</strong>{" "}
                      {new Date(detailEvent.waktu_mulai).toLocaleString(
                        "id-ID",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </MDTypography>
                    {detailEvent.waktu_finish && (
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Waktu Selesai:</strong>{" "}
                        {new Date(detailEvent.waktu_finish).toLocaleString(
                          "id-ID",
                          {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox mb={1}>
                    <MDTypography variant="body2" color="text.secondary">
                      <strong>Tempat:</strong> {detailEvent.tempat || "-"}
                    </MDTypography>
                  </MDBox>
                </>
              )}
            </MDBox>

            {/* Pesan jika tanggal tidak sesuai */}
            {detailEvent && !isDateValid && (
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "warning.lighter",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "warning.main",
                }}
              >
                <MDTypography variant="body2" color="warning.dark">
                  <strong>Perhatian:</strong> Event ini dijadwalkan pada{" "}
                  {new Date(detailEvent.waktu_mulai).toLocaleDateString(
                    "id-ID",
                    {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }
                  )}
                  . Silakan kembali pada tanggal tersebut untuk mengisi daftar
                  hadir.
                </MDTypography>
              </MDBox>
            )}

            <MDTypography variant="body2" color="text.secondary" mb={3}>
              Silakan isi form di bawah ini untuk mengisi daftar hadir.
            </MDTypography>

            {/* Autocomplete untuk memilih detail event jika lebih dari satu */}
            {availableDetailEvents.length > 1 && (
              <MDBox mb={3}>
                <Autocomplete
                  options={availableDetailEvents}
                  value={detailEvent}
                  onChange={handleSelectDetailEvent}
                  getOptionLabel={getDetailEventLabel}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Pilih Detail Event *"
                      variant="outlined"
                      fullWidth
                      required
                    />
                  )}
                  renderOption={(props, option) => {
                    // Tampilkan nama dinas dari dinas_id, bukan tempat
                    const namaDinas = option.dinas_id
                      ? dinasMap[option.dinas_id] ||
                        `Dinas ID: ${option.dinas_id}`
                      : "-";
                    return (
                      <li {...props} key={option.id}>
                        <MDBox>
                          <MDTypography variant="body2" fontWeight="medium">
                            {option.detail_kegiatan || "Detail Kegiatan"}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            {new Date(option.waktu_mulai).toLocaleString(
                              "id-ID",
                              {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                            {option.waktu_finish &&
                              ` - ${new Date(
                                option.waktu_finish
                              ).toLocaleString("id-ID", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`}
                            {` | ${namaDinas}`}
                          </MDTypography>
                        </MDBox>
                      </li>
                    );
                  }}
                />
              </MDBox>
            )}

            <MDBox mb={3}>
              <MDInput
                label="Nama *"
                name="nama"
                value={formData.nama}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
                disabled={isFormDisabled}
              />
            </MDBox>

            <MDBox mb={3}>
              <Autocomplete
                freeSolo={!isUserLoggedIn && !isFormDisabled} // Hanya allow freeSolo jika user tidak login dan form tidak disabled
                options={dinasList}
                value={formData.instansi || null}
                onChange={handleInstansiChange}
                onInputChange={(event, newInputValue) => {
                  if (!isUserLoggedIn && !isFormDisabled) {
                    // Hanya allow input manual jika user tidak login dan form tidak disabled
                    setFormData((prev) => ({
                      ...prev,
                      instansi: newInputValue || "",
                    }));
                  }
                }}
                disabled={isUserLoggedIn || isFormDisabled} // Disable jika user sudah login atau form disabled
                getOptionLabel={(option) => option || ""}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Instansi"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </MDBox>

            <MDBox display="flex" justifyContent="flex-end" mt={4}>
              <MDButton
                variant="gradient"
                color="success"
                onClick={handleSubmit}
                disabled={submitting || !isDateValid || !detailEvent}
                startIcon={
                  submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Icon>check_circle</Icon>
                  )
                }
              >
                {submitting ? "Mengirim..." : "Kirim Daftar Hadir"}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        {/* Dialog Rating Bintang */}
        <Dialog
          open={ratingDialogOpen}
          onClose={() => {}} // Disable close on backdrop click
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5" color="text" fontWeight="bold">
                Penilaian Event
              </MDTypography>
              <IconButton
                onClick={handleCancelRating}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox textAlign="center" py={2}>
              <MDTypography variant="body2" color="text" mb={2}>
                Silakan berikan penilaian Anda terhadap event yang Anda ikuti:
              </MDTypography>
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                sx={{
                  py: 3,
                }}
              >
                {[1, 2, 3, 4, 5].map((rating) => (
                  <MDBox
                    key={rating}
                    onClick={() => handleRatingClick(rating)}
                    sx={{
                      cursor: "pointer",
                      width: "75px",
                      height: "75px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s",
                      },
                      transition: "transform 0.2s",
                      "@media (max-width: 600px)": {
                        width: "50px",
                        height: "50px",
                      },
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="60"
                      height="60"
                      fill={selectedRating >= rating ? "#FFD700" : "#E0E0E0"}
                      style={{
                        transition: "fill 0.2s",
                      }}
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </MDBox>
                ))}
              </MDBox>
              {selectedRating > 0 && (
                <MDTypography
                  variant="body2"
                  color="text.secondary"
                  mt={2}
                  sx={{ fontStyle: "italic" }}
                >
                  Anda memberikan {selectedRating} bintang
                  {selectedRating === 1
                    ? " (Sangat Tidak Puas)"
                    : selectedRating === 2
                    ? " (Tidak Puas)"
                    : selectedRating === 3
                    ? " (Cukup)"
                    : selectedRating === 4
                    ? " (Puas)"
                    : " (Sangat Puas)"}
                </MDTypography>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={handleCancelRating}
            >
              Batal
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleRatingSubmit}
              disabled={selectedRating === 0 || submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Icon>send</Icon>
                )
              }
            >
              {submitting ? "Mengirim..." : "Kirim"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
}

export default DaftarHadirEventPublik;
